import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Plus, X, Upload, Sparkles, Loader, Search, Check, Info, Music } from "lucide-react";
import AdminLayout from "../../components/admin/AdminLayout";
import api, { BACKEND_URL } from "../../utils/api";

const RELEASE_TYPES = ["Single", "EP", "Album", "Compilation"];

// ── Artist Autocomplete ──────────────────────────────────────────
function ArtistAutocomplete({ value, onChange, inputCls }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const close = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const search = (q) => {
    clearTimeout(timerRef.current);
    if (!q.trim()) { setSuggestions([]); setOpen(false); return; }
    timerRef.current = setTimeout(async () => {
      try {
        const res = await api.get(`/artists/?search=${encodeURIComponent(q)}&limit=6`);
        const items = res.data.items || [];
        setSuggestions(items);
        setOpen(items.length > 0);
      } catch { setSuggestions([]); }
    }, 280);
  };

  return (
    <div ref={wrapRef} className="relative">
      <input
        value={value}
        onChange={(e) => { onChange(e.target.value); search(e.target.value); }}
        onFocus={() => { if (value) search(value); }}
        required
        className={inputCls}
        placeholder="Künstlername"
        data-testid="field-interpret"
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-zinc-950 border border-zinc-700 rounded-xl shadow-2xl overflow-hidden">
          {suggestions.map((a) => (
            <button
              key={a.id}
              type="button"
              onMouseDown={() => { onChange(a.name); setSuggestions([]); setOpen(false); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-zinc-800 transition-colors text-left border-b border-white/5 last:border-0"
            >
              <div className="w-7 h-7 rounded-full overflow-hidden bg-zinc-800 flex-shrink-0 flex items-center justify-center">
                {a.bild_url
                  ? <img src={a.bild_url} alt="" className="w-full h-full object-cover" />
                  : <span className="text-zinc-500 text-xs font-bold">{a.name[0]}</span>
                }
              </div>
              <span className="text-white text-sm">{a.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Label Autocomplete ───────────────────────────────────────────
function LabelAutocomplete({ value, onChange, inputCls }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const close = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const search = (q) => {
    clearTimeout(timerRef.current);
    if (!q.trim()) { setSuggestions([]); setOpen(false); return; }
    timerRef.current = setTimeout(async () => {
      try {
        const res = await api.get(`/labels/?search=${encodeURIComponent(q)}&limit=8`);
        const items = res.data.labels || [];
        setSuggestions(items);
        setOpen(items.length > 0);
      } catch { setSuggestions([]); }
    }, 280);
  };

  return (
    <div ref={wrapRef} className="relative">
      <input
        value={value}
        onChange={(e) => { onChange(e.target.value); search(e.target.value); }}
        onFocus={() => { if (value) search(value); }}
        className={inputCls}
        placeholder="Label-Name"
        autoComplete="off"
        data-testid="field-label"
      />
      {open && suggestions.length > 0 && (
        <div className="absolute z-30 top-full left-0 right-0 mt-1 bg-zinc-950 border border-zinc-700 rounded-xl shadow-2xl overflow-hidden">
          {suggestions.map((l) => (
            <button
              key={l.id}
              type="button"
              onMouseDown={() => { onChange(l.name); setSuggestions([]); setOpen(false); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-zinc-800 transition-colors text-left border-b border-white/5 last:border-0"
            >
              <div className="w-7 h-7 rounded-md overflow-hidden bg-zinc-800 flex-shrink-0 flex items-center justify-center">
                {l.logo_url
                  ? <img src={l.logo_url} alt="" className="w-full h-full object-cover" />
                  : <span className="text-zinc-500 text-xs font-bold">{l.name[0]}</span>
                }
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-white text-sm">{l.name}</span>
                {l.release_count > 0 && <span className="text-zinc-600 text-xs ml-2">{l.release_count} Releases</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ReleaseFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    interpret: "", titel: "", label: "", erscheinungsdatum: "",
    typ: "", cover_url: "", beatport: "", spotify: "", spotify_preview_track: "", amazon: "", apple_music: "",
    youtube: "", deezer: "", junodownload: "",
    tracklist: [""], categories: [], tags: [],
  });
  const [catInput, setCatInput] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [coverPreview, setCoverPreview] = useState("");
  const [spotifyFetching, setSpotifyFetching] = useState(false);
  const [spotifyMsg, setSpotifyMsg] = useState("");
  const [spotifySearching, setSpotifySearching] = useState(false);
  const [spotifyResults, setSpotifyResults] = useState([]);
  const [spotifySearchMsg, setSpotifySearchMsg] = useState("");
  // Step 2: Track-Auswahl
  const [trackStepAlbum, setTrackStepAlbum] = useState(null);   // Album aus Step 1
  const [albumTracks, setAlbumTracks] = useState([]);
  const [albumTracksLoading, setAlbumTracksLoading] = useState(false);

  useEffect(() => {
    if (isEdit) {
      setLoading(true);
      api.get(`/releases/${id}`)
        .then((res) => {
          const r = res.data;
          setForm({
            interpret: r.interpret || "",
            titel: r.titel || "",
            label: r.label || "",
            erscheinungsdatum: r.erscheinungsdatum || "",
            typ: r.typ || "",
            cover_url: r.cover_url || "",
            beatport: r.beatport || "",
            spotify: r.spotify || "",
            spotify_preview_track: r.spotify_preview_track || "",
            amazon: r.amazon || "",
            apple_music: r.apple_music || "",
            youtube: r.youtube || "",
            deezer: r.deezer || "",
            junodownload: r.junodownload || "",
            tracklist: r.tracklist?.length ? r.tracklist : [""],
            categories: r.categories || [],
            tags: r.tags || [],
          });
          setCoverPreview(r.cover_url || "");
        })
        .catch(() => navigate("/admin/releases"))
        .finally(() => setLoading(false));
    }
  }, [id, isEdit, navigate]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleTrackChange = (i, val) => {
    setForm((f) => {
      const tl = [...f.tracklist];
      tl[i] = val;
      return { ...f, tracklist: tl };
    });
  };
  const addTrack = () => setForm((f) => ({ ...f, tracklist: [...f.tracklist, ""] }));
  const removeTrack = (i) => setForm((f) => ({ ...f, tracklist: f.tracklist.filter((_, idx) => idx !== i) }));

  const addCategory = (e) => {
    e.preventDefault();
    const val = catInput.trim();
    if (val && !form.categories.includes(val)) {
      setForm((f) => ({ ...f, categories: [...f.categories, val] }));
    }
    setCatInput("");
  };
  const removeCategory = (cat) => setForm((f) => ({ ...f, categories: f.categories.filter((c) => c !== cat) }));

  const addTag = (e) => {
    e.preventDefault();
    const val = tagInput.trim();
    if (val && !form.tags.includes(val)) {
      setForm((f) => ({ ...f, tags: [...f.tags, val] }));
    }
    setTagInput("");
  };
  const removeTag = (tag) => setForm((f) => ({ ...f, tags: f.tags.filter((t) => t !== tag) }));

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post("/upload/image", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, cover_url: res.data.url }));
      setCoverPreview(res.data.url);
    } catch {
      alert("Fehler beim Hochladen");
    }
  };

  const handleSpotifyFetch = async () => {
    if (!form.spotify) return;
    setSpotifyFetching(true);
    setSpotifyMsg("");
    try {
      const res = await api.get(`/spotify/metadata?url=${encodeURIComponent(form.spotify)}`);
      const { cover_url, name, artist } = res.data;
      const updates = {};
      if (cover_url) { updates.cover_url = cover_url; setCoverPreview(cover_url); }
      if (artist && !form.interpret) updates.interpret = artist;
      if (name && !form.titel) updates.titel = name;
      setForm((f) => ({ ...f, ...updates }));
      setSpotifyMsg(cover_url ? "Cover erfolgreich geladen!" : "Kein Cover gefunden.");
      setTimeout(() => setSpotifyMsg(""), 4000);
    } catch (err) {
      setSpotifyMsg(err.response?.data?.detail || "Fehler beim Laden von Spotify");
      setTimeout(() => setSpotifyMsg(""), 4000);
    } finally {
      setSpotifyFetching(false);
    }
  };

  const handleSpotifySearch = async () => {
    // Strip featured artists ("feat.", "ft.") from interpret – they confuse Spotify's search
    const cleanInterpret = (form.interpret || "")
      .replace(/\s+feat\.?\s+.*/i, "")
      .replace(/\s+ft\.?\s+.*/i, "")
      .trim();
    const q = [cleanInterpret, form.titel].filter(Boolean).join(" ").trim();
    if (!q) return;
    setSpotifySearching(true);
    setSpotifyResults([]);
    setSpotifySearchMsg(`Suche: "${q}"`);
    try {
      const res = await api.get(`/spotify/search?q=${encodeURIComponent(q)}`);
      const results = res.data || [];
      setSpotifyResults(results);
      if (!results.length) setSpotifySearchMsg(`Keine Ergebnisse für "${q}".`);
    } catch (err) {
      setSpotifySearchMsg(err.response?.data?.detail || "Spotify-Suche fehlgeschlagen.");
    } finally {
      setSpotifySearching(false);
    }
  };

  // Step 1: Album ausgewählt → Tracks laden für Schritt 2
  const openTrackStep = async (result) => {
    setTrackStepAlbum(result);
    setAlbumTracks([]);
    setAlbumTracksLoading(true);
    try {
      const res = await api.get(`/spotify/album-tracks?url=${encodeURIComponent(result.spotify_url)}`);
      setAlbumTracks(res.data || []);
    } catch {
      setAlbumTracks([]);
    } finally {
      setAlbumTracksLoading(false);
    }
  };

  // Step 2: Track gewählt → Metadaten übernehmen
  const applySpotifyResult = (album, trackUrl = null, trackName = null) => {
    setForm((f) => ({
      ...f,
      cover_url: album.cover_url || f.cover_url,
      spotify: album.spotify_url || f.spotify,
      spotify_preview_track: trackUrl || "",   // leer = Auto (erster Track)
    }));
    if (album.cover_url) setCoverPreview(album.cover_url);
    setSpotifyResults([]);
    setTrackStepAlbum(null);
    setAlbumTracks([]);
    const label = trackName ? `"${trackName}" als Vorschau gesetzt!` : "Metadaten übernommen (Auto-Track)!";
    setSpotifySearchMsg(label);
    setTimeout(() => setSpotifySearchMsg(""), 4000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const payload = {
        ...form,
        tracklist: form.tracklist.filter(Boolean),
      };
      if (isEdit) {
        await api.put(`/releases/${id}`, payload);
      } else {
        await api.post("/releases/", payload);
      }
      navigate("/admin/releases");
    } catch (err) {
      setError(err.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full bg-zinc-900 border border-zinc-800 text-white rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-rose-600 transition-colors placeholder:text-zinc-600";
  const labelCls = "block text-zinc-400 text-xs font-bold uppercase tracking-wider mb-1.5";

  if (loading) return (
    <AdminLayout>
      <div className="animate-pulse space-y-4 max-w-3xl">
        {[1,2,3,4].map(i => <div key={i} className="h-12 skeleton rounded-md" />)}
      </div>
    </AdminLayout>
  );

  return (
    <AdminLayout>
      <div className="p-8">
      <div className="max-w-3xl">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/admin/releases" className="text-zinc-500 hover:text-white transition-colors text-sm">Releases</Link>
          <span className="text-zinc-700">/</span>
          <h1 className="text-xl font-black text-white font-heading">{isEdit ? "Release bearbeiten" : "Neuer Release"}</h1>
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-800/40 text-red-400 text-sm px-3 py-2 rounded-md mb-4">{error}</div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Interpret *</label>
              <ArtistAutocomplete
                value={form.interpret}
                onChange={(val) => setForm(f => ({ ...f, interpret: val }))}
                inputCls={inputCls}
              />
              <p className="flex items-center gap-1.5 text-zinc-600 text-xs mt-1.5">
                <Info size={11} className="flex-shrink-0" />
                Mehrere Künstler mit <code className="bg-zinc-800 px-1 rounded text-zinc-400">&amp;</code> oder <code className="bg-zinc-800 px-1 rounded text-zinc-400">x</code> trennen, nicht mit Komma
              </p>
            </div>
            <div>
              <label className={labelCls}>Titel *</label>
              <input value={form.titel} onChange={set("titel")} required className={inputCls} placeholder="Release-Titel" data-testid="field-titel" />
            </div>
            <div>
              <label className={labelCls}>Label</label>
              <LabelAutocomplete
                value={form.label}
                onChange={(val) => setForm(f => ({ ...f, label: val }))}
                inputCls={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Erscheinungsdatum</label>
              <input type="date" value={form.erscheinungsdatum} onChange={set("erscheinungsdatum")} className={inputCls} data-testid="field-date" />
            </div>
            <div>
              <label className={labelCls}>Typ</label>
              <select value={form.typ} onChange={set("typ")} className={`${inputCls} appearance-none`} data-testid="field-typ">
                <option value="">Typ wählen</option>
                {RELEASE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          {/* Cover */}
          <div className="bg-zinc-900 border border-white/5 rounded-xl p-4">
            <label className={labelCls}>Cover-Bild</label>
            <div className="flex gap-4">
              {coverPreview ? (
                <div className="w-24 h-24 rounded-lg overflow-hidden bg-zinc-800 flex-shrink-0">
                  <img src={coverPreview} alt="Cover" className="w-full h-full object-cover" onError={() => setCoverPreview("")} />
                </div>
              ) : (
                <div className="w-24 h-24 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0">
                  <Upload size={20} className="text-zinc-600" />
                </div>
              )}
              <div className="flex-1 space-y-2">
                <input value={form.cover_url} onChange={(e) => { set("cover_url")(e); setCoverPreview(e.target.value); }}
                  className={inputCls} placeholder="https://... oder Datei hochladen" data-testid="field-cover-url" />
                <button type="button" onClick={() => fileInputRef.current?.click()}
                  className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs rounded-md transition-colors flex items-center gap-1.5">
                  <Upload size={13} /> Datei hochladen
                </button>
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
              </div>
            </div>

            {/* ── Spotify-Suche ────────────────────────────────────── */}
            {(form.interpret || form.titel) && (
              <div className="mt-3 pt-3 border-t border-white/[0.05]">
                <div className="flex items-center gap-2">
                  <span className="text-zinc-500 text-xs">Cover &amp; Link via Spotify suchen:</span>
                  <button
                    type="button"
                    onClick={handleSpotifySearch}
                    disabled={spotifySearching}
                    data-testid="spotify-search-btn"
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white border border-green-600 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
                  >
                    {spotifySearching
                      ? <Loader size={11} className="animate-spin" />
                      : <Search size={11} />}
                    {spotifySearching ? "Suche..." : "Spotify suchen"}
                  </button>
                  {spotifyResults.length > 0 && (
                    <button
                      type="button"
                      onClick={() => { setSpotifyResults([]); setSpotifySearchMsg(""); }}
                      className="text-zinc-600 hover:text-zinc-400 text-xs transition-colors"
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>

                {/* Search results – Step 1: Album wählen */}
                {spotifyResults.length > 0 && !trackStepAlbum && (
                  <div className="mt-2 space-y-1.5">
                    {spotifyResults.map((result, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg bg-zinc-800/60 border border-white/[0.04] hover:border-white/10 transition-colors"
                      >
                        <div className="w-10 h-10 rounded overflow-hidden bg-zinc-700 flex-shrink-0">
                          {result.cover_url
                            ? <img src={result.cover_url} alt="" className="w-full h-full object-cover" />
                            : <div className="w-full h-full" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-xs font-semibold truncate">
                            {result.artist} – {result.name}
                          </p>
                          <p className="text-zinc-500 text-xs">
                            {result.release_date?.slice(0, 4)}
                            {result.album_type && ` · ${result.album_type}`}
                          </p>
                        </div>
                        <div className="flex gap-1.5 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => applySpotifyResult(result)}
                            data-testid={`spotify-apply-${i}`}
                            className="flex items-center gap-1 px-2.5 py-1 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 text-xs font-semibold rounded transition-colors"
                            title="Übernehmen (Auto-Track)"
                          >
                            <Check size={10} /> Auto
                          </button>
                          <button
                            type="button"
                            onClick={() => openTrackStep(result)}
                            data-testid={`spotify-track-step-${i}`}
                            className="flex items-center gap-1 px-2.5 py-1 bg-green-700 hover:bg-green-600 text-white text-xs font-semibold rounded transition-colors"
                            title="Track für Vorschau auswählen"
                          >
                            <Music size={10} /> Track wählen
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Step 2: Track-Auswahl für ausgewähltes Album */}
                {trackStepAlbum && (
                  <div className="mt-2 rounded-xl border border-green-800/40 overflow-hidden" style={{ background: "rgba(21,128,61,0.06)" }}>
                    {/* Album-Header */}
                    <div className="flex items-center gap-3 px-3 py-2.5 border-b border-green-800/30">
                      <div className="w-9 h-9 rounded overflow-hidden flex-shrink-0">
                        {trackStepAlbum.cover_url && <img src={trackStepAlbum.cover_url} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-xs font-bold truncate">{trackStepAlbum.artist} – {trackStepAlbum.name}</p>
                        <p className="text-zinc-500 text-[11px]">Wähle den Track für die 30s-Vorschau</p>
                      </div>
                      <button type="button" onClick={() => { setTrackStepAlbum(null); setAlbumTracks([]); }}
                        className="text-zinc-500 hover:text-zinc-300 transition-colors">
                        <X size={13} />
                      </button>
                    </div>

                    {albumTracksLoading ? (
                      <div className="flex justify-center py-4">
                        <Loader size={14} className="animate-spin text-green-500" />
                      </div>
                    ) : (
                      <div className="max-h-52 overflow-y-auto divide-y divide-white/[0.04]">
                        {/* Auto-Option */}
                        <button type="button" onClick={() => applySpotifyResult(trackStepAlbum)}
                          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-white/[0.04] transition-colors text-left">
                          <span className="w-5 text-zinc-600 text-[10px] font-mono text-right flex-shrink-0">–</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-zinc-400 text-xs italic">Auto (erster verfügbarer Track)</p>
                          </div>
                          <Check size={10} className="text-zinc-500 flex-shrink-0" />
                        </button>
                        {albumTracks.map((track, ti) => (
                          <button type="button" key={ti}
                            onClick={() => applySpotifyResult(trackStepAlbum, track.spotify_url, track.name)}
                            data-testid={`track-select-${ti}`}
                            className="w-full flex items-center gap-3 px-3 py-2 hover:bg-green-900/20 active:bg-green-800/30 transition-colors text-left group">
                            <span className="w-5 text-zinc-600 text-[10px] font-mono text-right flex-shrink-0">{track.track_number}</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-white text-xs font-medium truncate group-hover:text-green-400 transition-colors">
                                {track.name}
                              </p>
                              {track.artists && track.artists !== trackStepAlbum.artist && (
                                <p className="text-zinc-600 text-[10px] truncate">{track.artists}</p>
                              )}
                            </div>
                            {track.has_preview
                              ? <Music size={9} className="text-green-500 flex-shrink-0" title="30s-Vorschau verfügbar" />
                              : <span className="text-[9px] text-zinc-600 flex-shrink-0" title="Nur Embed, kein 30s-Audio">Embed</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {spotifySearchMsg && (
                  <p className={`text-xs mt-2 ${spotifySearchMsg.includes("übernommen") ? "text-green-400" : "text-amber-400"}`}>
                    {spotifySearchMsg}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Links */}
          <div className="bg-zinc-900 border border-white/5 rounded-xl p-4 space-y-4">
            <p className="text-zinc-400 text-xs font-bold uppercase tracking-wider">Links</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Beatport URL</label>
                <input value={form.beatport} onChange={set("beatport")} className={inputCls} placeholder="https://www.beatport.com/..." data-testid="field-beatport" />
              </div>
              <div>
                <label className={`${labelCls} flex items-center gap-2`}>
                  Spotify URL
                  {form.spotify && (
                    <button
                      type="button"
                      onClick={handleSpotifyFetch}
                      disabled={spotifyFetching}
                      data-testid="spotify-fetch-btn"
                      className="flex items-center gap-1 px-2 py-0.5 bg-green-600 hover:bg-green-500 text-white border border-green-600 rounded text-xs font-semibold transition-colors disabled:opacity-50"
                    >
                      {spotifyFetching ? <Loader size={10} className="animate-spin" /> : <Sparkles size={10} />}
                      Von Spotify laden
                    </button>
                  )}
                </label>
                <input value={form.spotify} onChange={set("spotify")} className={inputCls} placeholder="https://open.spotify.com/..." data-testid="field-spotify" />
                {/* Gewählter Vorschau-Track */}
                {form.spotify_preview_track && (
                  <div className="mt-1 flex items-center gap-1.5 text-xs text-green-500">
                    <Music size={10} />
                    <span className="truncate">Vorschau-Track: <span className="font-mono text-[10px] text-zinc-400">{form.spotify_preview_track.split("/track/")[1]?.slice(0, 12)}...</span></span>
                    <button type="button" onClick={() => setForm(f => ({ ...f, spotify_preview_track: "" }))}
                      className="ml-auto text-zinc-600 hover:text-red-500 transition-colors flex-shrink-0" title="Reset auf Auto">
                      <X size={9} />
                    </button>
                  </div>
                )}
                {spotifyMsg && (
                  <p className={`text-xs mt-1 ${spotifyMsg.includes("!") ? "text-green-400" : "text-amber-400"}`}>{spotifyMsg}</p>
                )}
              </div>
              <div>
                <label className={labelCls}>Amazon Music URL</label>
                <input value={form.amazon} onChange={set("amazon")} className={inputCls} placeholder="https://music.amazon.de/..." data-testid="field-amazon" />
              </div>
              <div>
                <label className={labelCls}>Apple Music URL</label>
                <input value={form.apple_music} onChange={set("apple_music")} className={inputCls} placeholder="https://music.apple.com/..." data-testid="field-apple-music" />
              </div>
              <div>
                <label className={labelCls}>YouTube URL</label>
                <input value={form.youtube} onChange={set("youtube")} className={inputCls} placeholder="https://www.youtube.com/watch?v=..." data-testid="field-youtube" />
              </div>
              <div>
                <label className={labelCls}>Deezer URL</label>
                <input value={form.deezer} onChange={set("deezer")} className={inputCls} placeholder="https://www.deezer.com/de/album/..." data-testid="field-deezer" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Juno Download URL</label>
                <input value={form.junodownload} onChange={set("junodownload")} className={inputCls} placeholder="https://www.junodownload.com/..." data-testid="field-junodownload" />
              </div>
            </div>
          </div>

          {/* Tracklist */}
          <div>
            <label className={labelCls}>Tracklist</label>
            <div className="space-y-2">
              {form.tracklist.map((track, i) => (
                <div key={i} className="flex gap-2">
                  <span className="text-zinc-600 text-xs w-5 text-right pt-2.5 flex-shrink-0 font-mono">{i + 1}</span>
                  <input value={track} onChange={(e) => handleTrackChange(i, e.target.value)}
                    className={`${inputCls} flex-1`} placeholder={`Track ${i + 1}`} data-testid={`track-input-${i}`} />
                  <button type="button" onClick={() => removeTrack(i)} className="p-2 text-zinc-600 hover:text-red-400 transition-colors">
                    <X size={14} />
                  </button>
                </div>
              ))}
              <button type="button" onClick={addTrack}
                className="flex items-center gap-1.5 text-zinc-500 hover:text-white text-sm transition-colors mt-1">
                <Plus size={14} /> Track hinzufügen
              </button>
            </div>
          </div>

          {/* Categories */}
          <div>
            <label className={labelCls}>Kategorien</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {form.categories.map((cat) => (
                <span key={cat} className="flex items-center gap-1 bg-rose-900/30 text-rose-300 border border-rose-800/40 text-xs px-2 py-1 rounded-full">
                  {cat}
                  <button type="button" onClick={() => removeCategory(cat)} className="hover:text-white ml-0.5"><X size={10} /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={catInput} onChange={(e) => setCatInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCategory(e); } }}
                className={`${inputCls} flex-1`} placeholder="Kategorie eingeben + Enter" data-testid="category-input" />
              <button type="button" onClick={addCategory} className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-md text-sm transition-colors">
                <Plus size={14} />
              </button>
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className={labelCls}>Tags</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {form.tags.map((tag) => (
                <span key={tag} className="flex items-center gap-1 bg-zinc-800 text-zinc-300 text-xs px-2 py-1 rounded-full">
                  {tag}
                  <button type="button" onClick={() => removeTag(tag)} className="hover:text-white ml-0.5"><X size={10} /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(e); } }}
                className={`${inputCls} flex-1`} placeholder="Tag eingeben + Enter" />
              <button type="button" onClick={addTag} className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-md text-sm transition-colors">
                <Plus size={14} />
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} data-testid="save-release-btn"
              className="px-6 py-2.5 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white font-bold rounded-md transition-colors">
              {saving ? "Speichern..." : "Speichern"}
            </button>
            <Link to="/admin/releases"
              className="px-6 py-2.5 bg-zinc-800 hover:bg-zinc-700 text-white font-semibold rounded-md transition-colors">
              Abbrechen
            </Link>
          </div>
        </form>
      </div>
      </div>
    </AdminLayout>
  );
}
